import { appShell } from './appShell'

// Firebase Performance keeps at most 5 custom attributes per trace and drops
// the rest. A trace's own attributes come first; these fill the remaining
// slots in this order, so the least useful split is the one that falls off.
const MAX_ATTRIBUTES = 5

const COLD_KEY = 'beacon_visited'
const ACCOUNT_SIZE_KEY = 'beacon_perf_account_size'

export let isCold = false
try {
  isCold = !localStorage.getItem(COLD_KEY)
  if (isCold) localStorage.setItem(COLD_KEY, '1')
} catch {
  // localStorage may be blocked (private mode, storage policy) — leave isCold=false rather than crash
}

const swControlled =
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  !!navigator.serviceWorker.controller

export type DeviceTier = 'low' | 'mid' | 'high'

// deviceMemory is Chromium-only (and capped at 8); WebKit only reports cores.
export function deviceTier(): DeviceTier {
  const cores = navigator.hardwareConcurrency || 0
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory
  if ((memory !== undefined && memory <= 3) || (cores > 0 && cores <= 4)) return 'low'
  if ((memory === undefined || memory >= 8) && cores >= 8) return 'high'
  return 'mid'
}

function accountSizeBucket(artistInterests: number): string {
  if (artistInterests === 0) return '0'
  if (artistInterests <= 50) return '1_50'
  if (artistInterests <= 200) return '51_200'
  return '200_plus'
}

// Persisted so a session tags its traces before the interests have loaded —
// and sessions that never load them (most of them) still carry the bucket.
let accountSize = (() => {
  try {
    return localStorage.getItem(ACCOUNT_SIZE_KEY) ?? 'unknown'
  } catch {
    return 'unknown'
  }
})()

export function setPerfAccountSize(artistInterests: number) {
  const next = accountSizeBucket(artistInterests)
  if (next === accountSize) return
  accountSize = next
  try {
    localStorage.setItem(ACCOUNT_SIZE_KEY, next)
  } catch {
    // keep the in-memory value
  }
}

const shell = appShell()
const tier = deviceTier()
const cacheState = isCold ? 'cold' : swControlled ? 'warm_sw' : 'warm'

export function traceAttributes(own?: Record<string, string>): Record<string, string> {
  const merged: Record<string, string> = { ...own }
  const base: [string, string][] = [
    ['platform', shell],
    ['device_tier', tier],
    ['cache', cacheState],
    ['account_size', accountSize],
  ]
  for (const [key, value] of base) {
    if (Object.keys(merged).length >= MAX_ATTRIBUTES) break
    if (!(key in merged)) merged[key] = value
  }
  return merged
}

export const bootAttributes = { platform: shell, device_tier: tier, cache: cacheState }
